// Places the API's custom field columns among an Inspect table's built-in
// columns (spec 2026-10-01 inspect custom columns, section 4). Pure: no React,
// no store. Never throws -- a table must render even if a config is malformed.

var START = 'start';

function compareText(a, b) {
  var la = String(a || '').toLowerCase();
  var lb = String(b || '').toLowerCase();
  if (la < lb) return -1;
  if (la > lb) return 1;
  return 0;
}

function comparePlacements(a, b) {
  var byName = compareText(a.inspectionName, b.inspectionName);
  if (byName !== 0) return byName;
  var ua = String(a.inspectionUUID || '');
  var ub = String(b.inspectionUUID || '');
  if (ua < ub) return -1;
  if (ua > ub) return 1;
  return 0;
}

// The representative placement when a column is placed by several configs:
// the one whose inspectionName sorts first, then inspectionUUID.
export function pickPlacement(placements) {
  if (!Array.isArray(placements) || placements.length === 0) return null;
  var sorted = placements.filter(function (p) { return !!p; }).sort(comparePlacements);
  return sorted.length > 0 ? sorted[0] : null;
}

function tag(kind, descriptor) {
  return Object.assign({ kind: kind }, descriptor);
}

// Malformed columns (no key, duplicate key, no placements) are dropped with a
// warning so one bad config cannot take the table down.
function validCustomColumns(customColumns) {
  var seen = {};
  var valid = [];
  (Array.isArray(customColumns) ? customColumns : []).forEach(function (column) {
    if (!column || typeof column.key !== 'string' || !column.key) {
      console.warn('Inspect custom column dropped: no key', column);
      return;
    }
    if (seen[column.key]) {
      console.warn('Inspect custom column dropped: duplicate key', column.key);
      return;
    }
    var placement = pickPlacement(column.placements);
    if (!placement) {
      console.warn('Inspect custom column dropped: no placements', column.key);
      return;
    }
    seen[column.key] = true;
    valid.push({ column: column, placement: placement });
  });
  return valid;
}

function visibleBuiltins(builtins, visibleKeys) {
  var list = Array.isArray(builtins) ? builtins : [];
  if (!Array.isArray(visibleKeys)) return list.slice();
  return list.filter(function (b) { return b && visibleKeys.indexOf(b.key) !== -1; });
}

// The visible built-in key a column goes after, START for the front, or null
// for the end.
function resolveSlot(afterColumn, canonicalKeys, visibleKeySet) {
  if (afterColumn === START) return START;
  var at = canonicalKeys.indexOf(afterColumn);
  if (at === -1) return null;
  for (var i = at; i >= 0; i--) {
    if (visibleKeySet[canonicalKeys[i]]) return canonicalKeys[i];
  }
  return START;
}

function layout(builtins, customColumns, visibleKeys) {
  var shown = visibleBuiltins(builtins, visibleKeys);
  var canonicalKeys = (Array.isArray(builtins) ? builtins : []).map(function (b) { return b && b.key; });
  var visibleKeySet = {};
  shown.forEach(function (b) { visibleKeySet[b.key] = true; });

  var slots = {};
  var startSlot = [];
  var endSlot = [];
  validCustomColumns(customColumns).forEach(function (entry) {
    var slot = resolveSlot(entry.placement.afterColumn, canonicalKeys, visibleKeySet);
    if (slot === START) startSlot.push(entry);
    else if (slot === null) endSlot.push(entry);
    else (slots[slot] = slots[slot] || []).push(entry);
  });

  function ordered(entries) {
    return entries
      .slice()
      .sort(function (a, b) {
        var byName = compareText(a.placement.inspectionName, b.placement.inspectionName);
        if (byName !== 0) return byName;
        var byOrder = (Number(a.placement.order) || 0) - (Number(b.placement.order) || 0);
        if (byOrder !== 0) return byOrder;
        return a.column.key < b.column.key ? -1 : a.column.key > b.column.key ? 1 : 0;
      })
      .map(function (e) { return tag('custom', e.column); });
  }

  var out = ordered(startSlot);
  shown.forEach(function (b) {
    out.push(tag('builtin', b));
    if (slots[b.key]) out = out.concat(ordered(slots[b.key]));
  });
  return out.concat(ordered(endSlot));
}

export default function layoutInspectColumns(builtins, customColumns, options) {
  var visibleKeys = options && options.visibleKeys;
  try {
    return layout(builtins, customColumns, visibleKeys);
  } catch (e) {
    console.warn('Inspect custom column layout failed; custom columns moved to the end', e);
    var fallback = visibleBuiltins(builtins, visibleKeys).map(function (b) { return tag('builtin', b); });
    var seen = {};
    (Array.isArray(customColumns) ? customColumns : []).forEach(function (column) {
      if (column && typeof column.key === 'string' && column.key && !seen[column.key]) {
        seen[column.key] = true;
        fallback.push(tag('custom', column));
      }
    });
    return fallback;
  }
}
